import React, { useContext, useEffect, useState } from 'react'
import _ from 'lodash'
import { jsPDF } from "jspdf";
import { useParams, useNavigate } from "react-router-dom"
import { Button, Form, Input, Spin, SelectProps, Select, InputNumber, App, Upload, TreeSelect, Switch, Divider, TimePicker, Space, Typography } from "antd"
import { DatePicker } from "antd-jalali"
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaPrint, FaRegSave, FaInbox } from "react-icons/fa"
import { API, Enum, Regex, Font } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios";

// Interfaces
import { AxiosError } from "../../../interfaces/axios"

const { Pages: { TicketManagement: { Priority: PriorityLanguage } }, Enum: EnumLanguage, Share, Validation, Message: MessageLanguage } = language
const { BASIC_INFORMATION_PROVINCE_CITIES, TICKET_MANAGEMENT_PRIORITY_ID, TICKET_MANAGEMENT_PRIORITY_CREATE, TICKET_MANAGEMENT_PRIORITY_UPDATE, TICKET_MANAGEMENT_COMPANY_ALL } = API
const { Message, Payment } = Enum

interface ProvincesType {
    id: string,
    name: string,
    cities: CityType[],
}

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    user: UserType,
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

interface PriorityType {
    row: number,
    id: string,
    key: string,
    destination : CityType,
    company: CompanyType,
    seat: number,
    operator?: UserType,
    createdAt: Date,
    updatedAt: Date,
}

interface PriorityFormType {
    destination: string,
    company: string,
    seat: number,
}

export default function CURecord() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const navigate = useNavigate()
    const { accessToken, baseURL, meta } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const [loadingProvinces, setLoadingProvinces] = useState<boolean>(true)
    const [provinces, setProvinces] = useState<ProvincesType[]>([])
    const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false)
    const [companies, setCompanies] = useState<CompanyType[]>([])
    const [loadingRecord, setLoadingRecord] = useState<boolean>(false)
    const { id: priorityId } = useParams()
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({})
        initialAPI().then(response => response)
    }, [])

    const initialAPI = async () => {
        await getProvinces()
        if (priorityId) {
            setLoadingRecord(true);
            await getRecord(priorityId)
        }
    }

    const getCompanies = async (city: string) => {
        setLoadingCompanies(true);
        await axios.post(TICKET_MANAGEMENT_COMPANY_ALL, { city, publish: true }).then((response: { data: CompanyType[] }) => {
            const { data: companies } = response
            setCompanies(companies)
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
        });
        setLoadingCompanies(false);
    }

    const getProvinces = async () => axios.get(BASIC_INFORMATION_PROVINCE_CITIES).then((response: { data: ProvincesType[] }) => {
        const { data: provinces } = response
        setProvinces(provinces)
        setLoadingProvinces(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const getRecord = async (priorityId: string): Promise<PriorityType> => axios.get(TICKET_MANAGEMENT_PRIORITY_ID + priorityId).then(async (response: { data: PriorityType }) => {
        const { data: priority } = response
        priority.destination.id && await getCompanies(priority.destination.id);
        form.setFieldsValue({ ...priority, destination: priority.destination.id, company: priority.company.id })
        setLoadingRecord(false)
        return priority;
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    })

    const onFinish = async (priority: PriorityFormType) => {
        setLoadingRecord(true);

        if (priorityId) {
            return axios.patch(TICKET_MANAGEMENT_PRIORITY_UPDATE + priorityId, priority).then(async (response: {}) => {
                message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                await getRecord(priorityId)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            return axios.post(TICKET_MANAGEMENT_PRIORITY_CREATE, priority).then(async (response: { data: { id: string } }) => {
                message.success(MessageLanguage[Message.CREATE_RECORD]).then(response => response)
                const { data: { id } } = response
                navigate('/ticket-management/priority/edit/' + id)
                await getRecord(id)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    }

    return (
        <div id={'priority'}>
            <Spin spinning={loadingRecord || loadingProvinces || loadingCompanies} tip={Share.loading} size="large">
                <Form form={form} name="cu-record" onFinish={onFinish} style={{ maxWidth: 450 }}>
                    <Form.Item
                        label={PriorityLanguage.destination}
                        name="destination"
                        hasFeedback
                        rules={[
                            { required: true, message: `${PriorityLanguage.destination} ${Validation.required}` },
                        ]}
                    >
                        <Select
                            showSearch
                            allowClear
                            placeholder={Share.pleaseSelectedRecords}
                            optionFilterProp="name"
                            onChange={async (destination) => {
                                form.resetFields(['company']);
                                setCompanies([]);
                                destination && await getCompanies(destination);
                            }}
                            options={provinces.map(({ name, cities }) => ({ label: name, options: cities.map(({ id, name }) => ({ label: name, value: id, name }))}))}
                        />
                    </Form.Item>
                    <Form.Item
                        label={PriorityLanguage.company}
                        name="company"
                        hasFeedback
                        rules={[
                            { required: true, message: `${PriorityLanguage.company} ${Validation.required}` },
                        ]}
                    >
                        <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder={Share.pleaseSelectedRecord}
                            options={companies.map(({ id, name }) => ({ label: name, value: id }))}
                        />
                    </Form.Item>
                    <Form.Item
                        label={PriorityLanguage.seat}
                        name="seat"
                        hasFeedback
                        rules={[
                            { required: true, message: `${PriorityLanguage.seat} ${Validation.required}` },
                        ]}
                    >
                        <InputNumber style={{width: '100%'}} min={1} max={3} />
                    </Form.Item>
                    <Form.Item shouldUpdate>
                        {() => (
                            <Button
                                icon={<FaRegSave />}
                                type="primary"
                                htmlType="submit"
                                block
                                disabled={
                                    !form.getFieldValue('destination') ||
                                    !form.getFieldValue('company') ||
                                    !form.getFieldValue('seat') ||
                                    !!form.getFieldsError().filter(({ errors }) => errors.length).length
                                }
                            >
                                { Share.save }
                            </Button>
                        )}
                    </Form.Item>
                </Form>
            </Spin>
        </div>
    )
}
